import React, { useMemo } from 'react';
import classNames from 'classnames';
import { Form, useFormikContext } from 'formik';
import { usePaymentContext } from '@/mp/hooks/usePaymentContext';
import text from '@/text';
import { useGetJobQuery } from '@/redux/rtk';
import { softwares } from '@/components/mapView/constants';
import { Control } from '@/components/form';
import TextDivider from '@/components/text/TextDivider';
import { useAppSelector } from '@/hooks/useAppSelector';
import { orderV2Selectors } from '@/redux/selectors/order.v2';
import { Col, Row } from 'react-bootstrap';
import { isJobPayableOrQuoted } from '@/utilities/job';
import Checkbox from '@/components/form/Checkbox';
import { ConfirmationFormValues } from '.';
import { Link } from 'react-router-dom';
import { getGroupText } from '@/utilities/order.v2';
import { groupBy } from '@larki/shared/object';
import { getProjectName } from '@/utilities/project';

const ConfirmationForm = ({ disabled }: { disabled?: boolean }) => {
  const { jobId, asMember, formatPrice } = usePaymentContext();
  const { data: job } = useGetJobQuery(jobId);
  const quote = useAppSelector((state) =>
    orderV2Selectors.getQuote(state, jobId, asMember)
  );
  const dataInDatabase = quote?.nonRfq.composition.data_in_database;
  const membership = quote?.nonRfq.composition.membership;
  const rfqCharges = useAppSelector((state) =>
    orderV2Selectors.getRfqCharges(state, jobId, asMember)
  );
  const customerSoftware = useAppSelector(orderV2Selectors.getCustomerSoftware);

  const overriddenSet = useAppSelector((state) =>
    orderV2Selectors.getOverriddenSet(state, jobId, asMember)
  );

  const totalNet =
    quote?.buyNowTotals?.totalNet ?? quote?.nonRfq.totals.totalNet;

  const taxRate = useMemo(() => {
    const taxes = quote?.nonRfq.totals.taxes ?? [];
    return taxes.reduce((sum, t) => sum + t.rate, 0);
  }, [quote?.nonRfq.totals.taxes]);

  const applyGst = (gross: number) => gross + Math.round(gross * taxRate);

  // Group totals are shown GST-inclusive so they line up visually with data_in_database rows.
  const overriddenGroups = useMemo(() => {
    if (overriddenSet.size === 0 || !rfqCharges) return [];
    const overridden = rfqCharges.filter((c) => overriddenSet.has(c.product));
    if (overridden.length === 0 && !overriddenSet.has('site_deployment_fee'))
      return [];

    const byGroup = groupBy(overridden, 'group');
    const groups: { label: string; total: number }[] = [];

    for (const [group, charges] of byGroup) {
      let groupGross = charges.reduce<number>(
        (sum, c) => sum + (typeof c.subtotal === 'number' ? c.subtotal : 0),
        0
      );
      if (
        group === 'capture_services' &&
        overriddenSet.has('site_deployment_fee')
      ) {
        const fees = quote?.rfq.composition.capture_services?.totals.fees ?? [];
        for (const fee of fees) {
          if (typeof fee.amount === 'number') groupGross += fee.amount;
        }
      }
      groups.push({ label: getGroupText(group), total: applyGst(groupGross) });
    }

    // Fee-only override: no capture_services charges but the fee still needs its own row.
    if (
      !byGroup.has('capture_services') &&
      overriddenSet.has('site_deployment_fee')
    ) {
      const fees = quote?.rfq.composition.capture_services?.totals.fees ?? [];
      let feeGross = 0;
      for (const fee of fees) {
        if (typeof fee.amount === 'number') feeGross += fee.amount;
      }
      if (feeGross > 0) {
        groups.push({
          label: getGroupText('capture_services'),
          total: applyGst(feeGross),
        });
      }
    }

    return groups;
  }, [rfqCharges, overriddenSet, quote, taxRate]);

  const overriddenCustomOrdersTotal = useMemo(() => {
    if (overriddenSet.size === 0) return 0;
    const gross = (quote?.customOrders ?? [])
      .filter((o) => overriddenSet.has(`custom_${o.id}`))
      .reduce((sum, o) => sum + (typeof o.price === 'number' ? o.price : 0), 0);
    return applyGst(gross);
  }, [quote?.customOrders, overriddenSet, taxRate]);

  const hasOverriddenCustomOrders = overriddenCustomOrdersTotal > 0;
  const { values, handleChange } = useFormikContext<ConfirmationFormValues>();
  return (
    <Form className='ConfirmationForm' aria-disabled={disabled}>
      <div className='orderSummary'>
        <h4
          className={classNames({
            'mt-2 mb-5 text-center': !jobId,
          })}
        >
          {text('confirmOrder')}
        </h4>
        {jobId ? (
          <>
            <div className='d-flex'>
              <p className='pr-3 font-weight-bold'>{text('projectName')}:</p>
              <p>{getProjectName(job?.project)}</p>
            </div>
            {customerSoftware ? (
              <div className='d-flex'>
                <p className='pr-3 font-weight-bold'>{text('yourSoftware')}:</p>
                <p>
                  {
                    softwares.entities[
                      customerSoftware as keyof typeof softwares.entities
                    ]?.name
                  }
                </p>
              </div>
            ) : null}
            {job?.message ? (
              <>
                <p>{text('comments')}:</p>
                {/* @ts-expect-error wrong types */}
                <Control
                  className='Message'
                  as='textarea'
                  rows={3}
                  value={job.message}
                  readOnly
                />
              </>
            ) : null}
          </>
        ) : null}
      </div>
      <div className='totalSummary'>
        {/* @ts-expect-error: wrong types */}
        <TextDivider isComplete />
        {dataInDatabase?.charges.length ? (
          <Row className='Price'>
            <Col xs={12} md={6}>
              <div className='PriceHeader font-weight--600'>
                {'1. '}
                {text(
                  isJobPayableOrQuoted(job)
                    ? 'dataInDatabase'
                    : 'dataPayLater'
                )}
              </div>
            </Col>
            <Col>
              <p className='text-right font-14 font-weight--600 font- mb-2 font-green--1'>
                {dataInDatabase?.totals.totalNet &&
                dataInDatabase.totals.totalNet !== 'TBC'
                  ? formatPrice(dataInDatabase.totals.totalNet)
                  : text('toBeConfirmed')}
              </p>
            </Col>
          </Row>
        ) : null}
        {overriddenGroups.map((group, i) => (
          <Row key={group.label} className='Price'>
            <Col xs={12} md={6}>
              <div className='PriceHeader  font-weight--600'>
                {`${(dataInDatabase?.charges.length ? 1 : 0) + i + 1}. `}
                {group.label}
              </div>
            </Col>
            <Col>
              <p className='text-right font-14 font-weight--600 font- mb-2 font-green--1'>
                {formatPrice(group.total)}
              </p>
            </Col>
          </Row>
        ))}
        {hasOverriddenCustomOrders && (
          <Row className='Price'>
            <Col xs={12} md={6}>
              <div className='PriceHeader font-weight--600'>
                {`${(dataInDatabase?.charges.length ? 1 : 0) + overriddenGroups.length + 1}. `}
                {text('customOrder')}s
              </div>
            </Col>
            <Col>
              <p className='text-right font-14 font-weight--600 font- mb-2 font-green--1'>
                {formatPrice(overriddenCustomOrdersTotal)}
              </p>
            </Col>
          </Row>
        )}
        {membership?.charges.length ? (
          <Row
            className={classNames('Price', {
              'pt-1': !jobId,
            })}
          >
            <Col xs={12} md={6}>
              <div className='PriceHeader font-weight--600'>
                {`${(dataInDatabase?.charges.length ? 1 : 0) + overriddenGroups.length + (hasOverriddenCustomOrders ? 1 : 0) + 1}. `}
                {!jobId ? `${text('invoice')} - ` : null} {text('membership')}
              </div>
            </Col>
            <Col>
              <p className='text-right font-14 font-weight--600 font- mb-2 font-green--1'>
                {membership?.totals.totalNet &&
                membership.totals.totalNet !== 'TBC'
                  ? formatPrice(membership.totals.totalNet)
                  : text('toBeConfirmed')}
              </p>
            </Col>
          </Row>
        ) : null}
      </div>

      {/* <hr />
      <Row>
        <Col xs={12} md={6}>
          <div className='PriceHeader  font-weight--600'>
            {text('totalToPayNow')}
          </div>
        </Col>
        <Col>
          <p className='text-right font-18 font-green--1 font-weight--600 mb-0'>
            {totalNet === 'TBC'
              ? text('toBeConfirmed')
              : totalNet
                ? formatPrice(totalNet)
                : ''}
          </p>
        </Col>
      </Row> */}
      <hr />

      {/* mt-4 d-flex flex-row flex-fill justify-content-center */}
      <div
        className={classNames('Terms', {
          'mt-4 mb-4 d-flex flex-row justify-content-center': !jobId,
        })}
      >
        <Checkbox
          onChange={handleChange}
          name='terms'
          id='terms'
          value={values.terms}
          labelClass='ml-3'
          disabled={disabled}
        >
          {text('acceptTermsAndConditions', {
            termsConditions: (
              <Link
                key='urlTerms'
                to={{ pathname: process.env.LARKI_URL_TERMS }}
                target='_blank'
              >
                {text('termsAndConditions')}
              </Link>
            ),
          })}
        </Checkbox>
      </div>
    </Form>
  );
};

export default ConfirmationForm;
